import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { apiClient } from "@/config/api";
import { BackendMessage, BackendMessagesResponse } from "@/types/chat";
import { RootState } from "@/types/store";
import {
  GetConversationAfterArchiveBody,
  GetConversationsBody,
} from "@/api/chat/types";
import { transformBackendConversations } from "@/lib/utils";
import { DRAFT_CONVERSATION_ID } from "@/utils/constants";

export const createChatConversation = createAsyncThunk(
  "/chat/conversations",
  async (homeownerId: string | undefined, { rejectWithValue, dispatch }) => {
    try {
      const requestBody = homeownerId ? { homeownerId } : {};
      const response = await apiClient.post("/chat/conversations", requestBody);

      const conversationId = response.data.data.id;

      // Fetch the updated conversations list to get the complete conversation data
      await dispatch(
        getChatConversations({
          page: 1,
          filterStartDate: null,
          filterEndDate: null,
          filterHomeowners: [],
        })
      );

      return { conversationId };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Create chat conversation failed"
        );
      }
      return rejectWithValue("Create chat conversation failed");
    }
  }
);

export const getChatConversations = createAsyncThunk(
  "/chat/conversations/get",
  async (
    {
      page,
      filterStartDate,
      filterEndDate,
      filterHomeowners,
      searchTerm,
    }: GetConversationsBody,
    { rejectWithValue }
  ) => {
    try {
      const params = {
        page: page ? page : undefined,
        startDate: filterStartDate ? filterStartDate : undefined,
        endDate: filterEndDate ? filterEndDate : undefined,
        homeownerIds: filterHomeowners.flatMap((homeowner) =>
          homeowner.selected ? [homeowner.id] : []
        ),
        searchTerm: searchTerm || undefined,
      };
      const response = await apiClient.get("/chat/conversations", { params });

      const transformedConversations = transformBackendConversations(
        response.data.data
      );

      return {
        conversations: transformedConversations,
        total: response.data.data.total,
        hasNextPage: response.data.data.hasNextPage,
        page: page ? page : 1,
        isLoadMore: false,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Get chat conversations failed"
        );
      }
      return rejectWithValue("Get chat conversations failed");
    }
  }
);

export const getChatConversationAfterRemoval = createAsyncThunk(
  "/chat/conversations/getAfterRemoval",
  async (
    {
      page,
      filterStartDate,
      filterEndDate,
      filterHomeowners,
      conversationType,
    }: GetConversationAfterArchiveBody,
    { rejectWithValue }
  ) => {
    try {
      const params = {
        page: page ? page : undefined,
        startDate: filterStartDate ? filterStartDate : undefined,
        endDate: filterEndDate ? filterEndDate : undefined,
        homeownerIds: filterHomeowners.flatMap((homeowner) =>
          homeowner.selected ? [homeowner.id] : []
        ),
      };
      const response = await apiClient.get("/chat/conversations", { params });

      const transformedConversations = transformBackendConversations(
        response.data.data
      );

      return {
        conversation:
          transformedConversations[transformedConversations.length - 1],
        total: response.data.data.total,
        hasNextPage: response.data.data.hasNextPage,
        page: page ? page : 1,
        isLoadMore: false,
        conversationType,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Get chat conversations failed"
        );
      }
      return rejectWithValue("Get chat conversations failed");
    }
  }
);

export const loadMoreChatConversations = createAsyncThunk(
  "/chat/conversations/loadMore",
  async (
    {
      page,
      filterStartDate,
      filterEndDate,
      filterHomeowners,
      searchTerm,
    }: GetConversationsBody,
    { rejectWithValue }
  ) => {
    try {
      const params = {
        page,
        startDate: filterStartDate ? filterStartDate : undefined,
        endDate: filterEndDate ? filterEndDate : undefined,
        homeownerIds: filterHomeowners.flatMap((homeowner) =>
          homeowner.selected ? [homeowner.id] : []
        ),
        searchTerm: searchTerm || undefined,
      };
      const response = await apiClient.get("/chat/conversations", {
        params,
      });

      const transformedConversations = transformBackendConversations(
        response.data.data
      );

      return {
        conversations: transformedConversations,
        total: response.data.data.total,
        hasNextPage: response.data.data.hasNextPage,
        page: page ? page : 1,
        isLoadMore: true,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Load more chat conversations failed"
        );
      }
      return rejectWithValue("Load more chat conversations failed");
    }
  }
);

export const getArchivedConversations = createAsyncThunk(
  "/chat/archived-conversations/get",
  async (
    {
      page,
      filterStartDate,
      filterEndDate,
      filterHomeowners,
      searchTerm,
    }: GetConversationsBody,
    { rejectWithValue }
  ) => {
    try {
      const params = {
        page: page ? page : undefined,
        startDate: filterStartDate ? filterStartDate : undefined,
        endDate: filterEndDate ? filterEndDate : undefined,
        homeownerIds: filterHomeowners.flatMap((homeowner) =>
          homeowner.selected ? [homeowner.id] : []
        ),
        searchTerm: searchTerm || undefined,
      };
      const response = await apiClient.get("/chat/archived-conversations", {
        params,
      });

      const transformedConversations = transformBackendConversations(
        response.data.data
      );

      return {
        conversations: transformedConversations,
        total: response.data.data.total,
        hasNextPage: response.data.data.hasNextPage,
        page: page ? page : 1,
        isLoadMore: false,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Get archived conversations failed"
        );
      }
      return rejectWithValue("Get archived conversations failed");
    }
  }
);

export const loadMoreArchivedConversations = createAsyncThunk(
  "/chat/archived-conversations/loadMore",
  async (
    {
      page,
      filterStartDate,
      filterEndDate,
      filterHomeowners,
      searchTerm,
    }: GetConversationsBody,
    { rejectWithValue }
  ) => {
    try {
      const params = {
        page,
        startDate: filterStartDate ? filterStartDate : undefined,
        endDate: filterEndDate ? filterEndDate : undefined,
        homeownerIds: filterHomeowners.flatMap((homeowner) =>
          homeowner.selected ? [homeowner.id] : []
        ),
        searchTerm: searchTerm || undefined,
      };
      const response = await apiClient.get("/chat/archived-conversations", {
        params,
      });

      const transformedConversations = transformBackendConversations(
        response.data.data
      );

      return {
        conversations: transformedConversations,
        total: response.data.data.total,
        hasNextPage: response.data.data.hasNextPage,
        page: page ? page : 1,
        isLoadMore: true,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Load more archived conversations failed"
        );
      }
      return rejectWithValue("Load more archived conversations failed");
    }
  }
);

export const archiveConversation = createAsyncThunk(
  "/chat/conversations/archive",
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await apiClient.post(`/chat/conversations/${conversationId}/archive`);
      return { conversationId };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Archive conversation failed"
        );
      }
      return rejectWithValue("Archive conversation failed");
    }
  }
);

export const unarchiveConversation = createAsyncThunk(
  "/chat/conversations/unarchive",
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await apiClient.post(`/chat/conversations/${conversationId}/unarchive`);
      return { conversationId };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Unarchive conversation failed"
        );
      }
      return rejectWithValue("Unarchive conversation failed");
    }
  }
);

export const getChatMessages = createAsyncThunk(
  "/chat/conversation/:conversationId/messages",
  async (
    { conversationId, page }: { conversationId: string; page?: string },
    { rejectWithValue, getState }
  ) => {
    try {
      if (conversationId === DRAFT_CONVERSATION_ID) {
        return {
          conversationId,
          messages: [],
          total: 0,
          hasNextPage: false,
          page: page ? parseInt(page) : 1,
        };
      }

      const params = page ? { page } : {};
      const response = await apiClient.get(
        `/chat/conversation/${conversationId}/messages`,
        { params }
      );

      // Get current user ID from state
      const state = getState() as RootState;
      const currentUserId = state.Auth.user?.id;

      const backendData: BackendMessagesResponse = response.data.data;
      const transformedMessages =
        backendData.messages?.map((message: BackendMessage) => {
          const isFromUser = message.senderId === currentUserId;
          const senderName = isFromUser
            ? "You"
            : [message.sender.firstName, message.sender.lastName]
                .filter(Boolean)
                .join(" ");

          return {
            id: message.id,
            text: message.content,
            sender: isFromUser ? ("user" as const) : ("other" as const),
            timestamp: message.createdAt,
            senderName,
            senderAvatar: message.sender.profilePicture || undefined,
            files:
              message.attachments?.map((attachment) => ({
                ...attachment.file,
                attachmentId: attachment.id,
              })) ?? [],
          };
        }) || [];

      return {
        conversationId,
        messages: transformedMessages,
        total: backendData.total || 0,
        hasNextPage: backendData.hasNextPage || false,
        page: page ? parseInt(page) : 1,
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get chat messages failed"
        );
      }
      return rejectWithValue("Get chat messages failed");
    }
  }
);

export const getUnreadMessagesCount = createAsyncThunk(
  "/chat/unread",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/chat/unread");
      return response.data.data.unreadCount;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Get unread messages count failed"
        );
      }
      return rejectWithValue("Get unread messages count failed");
    }
  }
);

// Attachment links are signed for 15 minutes, so a chat left open outlives them.
// This re-signs one on demand, keyed by the attachment rather than the file.
export const refreshChatAttachmentUrl = createAsyncThunk(
  "/chat/attachment/url",
  async (attachmentId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `/chat/attachment/${attachmentId}/url`
      );

      return response.data.data as { id: string; name: string; url: string };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Refresh attachment URL failed"
        );
      }
      return rejectWithValue("Refresh attachment URL failed");
    }
  }
);

export const deleteConversation = createAsyncThunk(
  "/chat/conversations/delete",
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/chat/conversation/${conversationId}`);
      return { conversationId };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Delete conversation failed"
        );
      }
      return rejectWithValue("Delete conversation failed");
    }
  }
);
